import {
  useCallback,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * A pop-out drawn at the document root and placed against its trigger (I-0401).
 *
 * ## Why the root
 *
 * A panel positioned inside its trigger's parent is clipped by every scrolling ancestor between the
 * two. The sidebar gained one for I-0395 (so a long page list can scroll) and the notification
 * panel, which opens rightwards over the page, was cut at the sidebar's edge at every width. Moving
 * the scroll container would fix that one case and leave the next scroll container to do it again,
 * so the panel leaves the tree instead: rendered into `document.body`, `position: fixed`, and placed
 * from the trigger's own box, nothing it sits inside can clip it.
 *
 * ## Why it follows
 *
 * A fixed panel no longer moves with its trigger, so it is re-placed when anything scrolls (capture
 * phase: an inner scroll container's scroll does not bubble), when the window resizes, and when the
 * trigger or the panel changes size. Flipped when its side has no room, and clamped to the viewport,
 * so a trigger near an edge still opens a whole panel.
 */

export type AnchoredPlacementType = 'top' | 'bottom';
/** `left`: the panel's left edge meets the trigger's; `right`: right edges meet; `stretch`: same width. */
export type AnchoredAlignType = 'left' | 'right' | 'stretch';

export interface IAnchoredOptions {
  placement: AnchoredPlacementType;
  align: AnchoredAlignType;
  /** Gap between trigger and panel. */
  offset?: number;
  /** Minimum distance kept from the viewport's edges. */
  margin?: number;
}

export interface IRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface IAnchoredPosition {
  top: number;
  left: number;
  width?: number;
  maxHeight: number;
  placement: AnchoredPlacementType;
}

/** Where the panel goes. Pure, so the geometry is tested without a browser. */
export function anchoredPosition(
  anchor: IRect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  { placement, align, offset = 6, margin = 8 }: IAnchoredOptions,
): IAnchoredPosition {
  const width = align === 'stretch' ? anchor.width : panel.width;
  const below = viewport.height - (anchor.top + anchor.height) - offset - margin;
  const above = anchor.top - offset - margin;
  // Flip only when the preferred side cannot hold the panel and the other side holds more of it.
  const side: AnchoredPlacementType =
    placement === 'top'
      ? above < panel.height && below > above
        ? 'bottom'
        : 'top'
      : below < panel.height && above > below
        ? 'top'
        : 'bottom';
  const room = Math.max(0, side === 'top' ? above : below);
  const height = Math.min(panel.height, room);
  const top = side === 'top' ? anchor.top - offset - height : anchor.top + anchor.height + offset;

  const preferredLeft = align === 'right' ? anchor.left + anchor.width - width : anchor.left;
  const left = Math.min(
    Math.max(margin, preferredLeft),
    Math.max(margin, viewport.width - margin - width),
  );

  return {
    top: Math.max(margin, top),
    left,
    ...(align === 'stretch' ? { width } : {}),
    maxHeight: room,
    placement: side,
  };
}

/**
 * The fixed style for a panel anchored to `anchor`, kept current while `open`. Until the panel has
 * been measured it is laid out but transparent, so it never flashes at the corner of the page.
 */
export function useAnchoredStyle(
  anchor: RefObject<HTMLElement | null>,
  panel: RefObject<HTMLElement | null>,
  open: boolean,
  options: IAnchoredOptions,
): CSSProperties {
  const { placement, align, offset, margin } = options;
  const [position, setPosition] = useState<IAnchoredPosition | null>(null);

  const place = useCallback(() => {
    const trigger = anchor.current;
    const element = panel.current;
    if (!trigger || !element) return;
    const box = trigger.getBoundingClientRect();
    setPosition(
      anchoredPosition(
        { top: box.top, left: box.left, width: box.width, height: box.height },
        // `scrollHeight`: the height the panel wants, not the height a previous max-height allowed.
        { width: element.offsetWidth, height: element.scrollHeight },
        { width: window.innerWidth, height: window.innerHeight },
        { placement, align, offset, margin },
      ),
    );
  }, [align, anchor, margin, offset, panel, placement]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    place();
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    window.addEventListener('resize', schedule);
    // Capture: a scroll inside the sidebar does not bubble to `window`, and it moves the trigger.
    window.addEventListener('scroll', schedule, true);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    if (anchor.current) observer?.observe(anchor.current);
    if (panel.current) observer?.observe(panel.current);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      observer?.disconnect();
    };
  }, [anchor, open, panel, place]);

  // Transparent, not `visibility: hidden`: a menu moves focus into itself as it opens, and an element
  // hidden that way cannot take focus during the frame before it is placed.
  if (!position) return { position: 'fixed', top: 0, left: 0, opacity: 0, pointerEvents: 'none' };
  return {
    position: 'fixed',
    top: position.top,
    left: position.left,
    ...(position.width !== undefined ? { width: position.width } : {}),
    maxHeight: position.maxHeight,
  };
}

/** Renders `children` into `document.body`. */
export function AnchoredPortal({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}
